import React from "react";
import { Alert, Box, Typography, TextField } from "@mui/material";
import { api } from "../../services/api";
import { FormRadio } from "../../components/ui";


const splitFieldNames = (value) =>
  String(value ?? "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

export const areStorageDropdownParentsReady = (field, formData, systemValues) => {
  const systemParents = {
    ActiveAccountID: systemValues.activeAccountId,
    ActiveAccountId: systemValues.activeAccountId,
    activeAccountId: systemValues.activeAccountId,
    accountId: systemValues.activeAccountId,
    subscriptionKey: systemValues.subscriptionKey,
    userId: systemValues.userId,
    ResourceType: systemValues.resourceType,
  };
  const urlParents = Array.from(
    String(field.dropdownSource ?? "").matchAll(/\{([^{}]+)\}/g),
    (match) => match[1]
  );
  const parents = new Set([...splitFieldNames(field.parentField), ...urlParents]);

  return Array.from(parents).every((parent) => {
    if (Object.prototype.hasOwnProperty.call(systemParents, parent)) {
      return String(systemParents[parent] ?? "").trim() !== "";
    }

    const raw = formData[parent]?.value;
    const first = Array.isArray(raw) ? raw[0] : raw;
    const value = first !== null && typeof first === "object"
      ? first.value ?? first.label ?? first.text
      : first;
    return value !== undefined && value !== null && String(value).trim() !== "";
  });
};

export const normalizeStorageAccountFormConfig = (formConfig) =>
  formConfig.map((field) => {
    const dependencies = splitFieldNames(field.dependentOn);
    if (!dependencies.length) return field;

    const urlDependencies = dependencies.filter((name) =>
      String(field.dropdownSource ?? "").includes(`{${name}}`)
    );
    if (!urlDependencies.length && dependencies.length === 1) return field;

    const visibilityDependencies = dependencies.filter(
      (name) => !urlDependencies.includes(name)
    );

    return {
      ...field,
      parentField: urlDependencies.length
        ? Array.from(new Set([...splitFieldNames(field.parentField), ...urlDependencies])).join(",")
        : field.parentField,
      dependentOn: visibilityDependencies.length
        ? visibilityDependencies.join(",") 
        : dependencies.join(","),
    };
  });

const isChecked = (value) =>
  value === true ||
  value === 1 ||
  (typeof value === "string" && value.toLowerCase() === "true");

const normalizeBoolLike = (value) => String(value ?? "").trim().toLowerCase();

const parseDependentRules = (dependentValues) => {
  if (!dependentValues) return [];

  if (Array.isArray(dependentValues)) {
    return dependentValues;
  }

  if (typeof dependentValues === "string") {
    try {
      const parsed = JSON.parse(dependentValues);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

const isValidIpv4 = (value) => {
  const parts = String(value).split(".");
  if (parts.length !== 4) return false;

  return parts.every((part) => {
    if (!/^\d+$/.test(part)) return false;
    if (part.length > 1 && part.startsWith("0")) return false;
    const num = Number(part);
    return num >= 0 && num <= 255;
  });
};

export const isAzureStorageNameField = (fieldName) =>
  String(fieldName ?? "").trim().toLowerCase() === "storageaccountname";

export const isAzureIpv4Field = (fieldName) =>
  String(fieldName ?? "").trim().toLowerCase() === "iprules";

export const getAzureStorageAccountNameError = (value) => {
  const name = String(value ?? "").trim();
  if (!name) return "Storage account name is required.";
  if (!/^[a-z0-9]{3,24}$/.test(name)) {
    return "Use 3-24 lowercase letters or numbers for the storage account name.";
  }
  return "";
};

export const getAzureIpv4OrCidrError = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const cidrParts = text.split("/");
  if (cidrParts.length > 2) {
    return "Enter a valid IPv4 address or CIDR range.";
  }

  const ipPart = cidrParts[0];
  if (!isValidIpv4(ipPart)) {
    return "Enter a valid IPv4 address.";
  }

  if (cidrParts.length === 2) {
    const prefix = cidrParts[1];
    if (!/^\d+$/.test(prefix)) {
      return "CIDR prefix must be a number between 0 and 32.";
    }

    const prefixNum = Number(prefix);
    if (prefixNum < 0 || prefixNum > 32) {
      return "CIDR prefix must be between 0 and 32.";
    }
  }

  return "";
};

export const getStorageConditionalValidation = (field, formData, formConfig = []) => {
  const rules = parseDependentRules(field?.dependentValues);
  if (!rules.length) {
    return {
      isRequired: false,
      expectedValue: null,
      message: "",
    };
  }

  const dependencyField = splitFieldNames(field?.dependentOn)[0];
  const parent = formConfig.find((candidate) =>
    candidate.fieldName.toLowerCase() === dependencyField?.toLowerCase()
  );
  const dependencyValue = dependencyField
    ? normalizeBoolLike(parent ? getStorageFieldValue(parent, formData) : formData?.[dependencyField]?.value)
    : "";

  const activeRule = rules.find((rule) => {
    if (rule?.isVisible === false) return false;
    if (rule?.value === undefined || rule?.value === null || rule?.value === "") return true;
    return dependencyValue === normalizeBoolLike(rule.value);
  });

  if (!activeRule) {
    return {
      isRequired: false,
      expectedValue: null,
      message: "",
    };
  }

  const expectedRaw = activeRule.defaultValue ?? activeRule.value ?? "true";

  return {
    isRequired: activeRule.isRequired === true,
    expectedValue: normalizeBoolLike(expectedRaw),
    message: activeRule.validationMessage || "",
  };
};

const isCheckboxDependencySatisfied = (field, formConfig, formData) => {
  const dependencies = splitFieldNames(field?.dependentOn);
  if (!dependencies.length) return true;

  const expectedValues = splitFieldNames(field?.dependentValue).map((value) => value.toLowerCase());

  return dependencies.every((dependencyName) => {
    const parent = formConfig.find(
      (candidate) => String(candidate.fieldName).trim().toLowerCase() === dependencyName.toLowerCase()
    );

    if (!parent || parent.fieldType !== "checkbox") {
      return true;
    }

    const parentValue = getStorageFieldValue(parent, formData);
    const normalizedParentValue = normalizeBoolLike(parentValue);

    if (expectedValues.length > 0) {
      return expectedValues.includes(normalizedParentValue);
    }

    return normalizedParentValue === "true";
  });
};

const getForcedCheckboxValue = (field, formData, formConfig) => {
  const conditionalValidation = getStorageConditionalValidation(field, formData, formConfig);
  if (!conditionalValidation.isRequired || conditionalValidation.expectedValue === null) {
    return null;
  }

  if (conditionalValidation.expectedValue === "true") return true;
  if (conditionalValidation.expectedValue === "false") return false;

  return null;
};

export const getStorageCheckboxState = (field, formConfig, formData) => {
  const restoreField = getPointInTimeRestoreField(formConfig);
  if (restoreField?.fieldName === field.fieldName) {
    const checked = isChecked(getStorageFieldValue(field, formData));
    return {
      checked,
      disabled: false,
      message: field.isRequired && !checked
        ? `${field.fieldLabel || field.fieldName} is required.`
        : "",
    };
  }

  const dependencyEnabled = isCheckboxDependencySatisfied(field, formConfig, formData);
  const forcedValue = dependencyEnabled ? getForcedCheckboxValue(field, formData, formConfig) : null;
  const checked = dependencyEnabled &&
    (forcedValue ?? isChecked(getStorageFieldValue(field, formData)));
  const conditional = getStorageConditionalValidation(field, formData, formConfig);
  const invalidConditional = dependencyEnabled && conditional.isRequired &&
    conditional.expectedValue !== null &&
    normalizeBoolLike(checked) !== conditional.expectedValue;
  const missing = dependencyEnabled && field.isRequired && !checked && forcedValue !== false;

  return {
    checked,
    disabled: !dependencyEnabled || forcedValue !== null,
    message: invalidConditional
      ? conditional.message || `${field.fieldLabel || field.fieldName} has an invalid value.`
      : missing ? `${field.fieldLabel || field.fieldName} is required.` : "",
  };
};

const isNumericDaysInput = (field) =>
  field.fieldType !== "checkbox" &&
  ["text", "number"].includes(field.fieldType) &&
  /days|restore|retention/i.test(field.fieldName);

export const getStorageDaysError = (field, formData) => {
  if (!isNumericDaysInput(field)) return "";
  const value = getStorageFieldValue(field, formData);
  if (String(value).trim() === "") return field.isRequired ? "Required" : "";
  return /^\d+$/.test(String(value)) && Number(value) >= 1 && Number(value) <= 365
    ? "" : "Enter a whole number from 1-365.";
};

export const isStorageDataProtectionField = (field) =>
  field.tabName === "Data Protection";

export const getStorageFieldValue = (field, formData) =>
  formData?.[field.fieldName]?.value ??
  field.defaultValue ??
  (field.fieldType === "checkbox" ? false : "");

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const normalizeKey = (value) =>
  String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const findStorageSetting = (formConfig, names) =>
  formConfig.find((field) => names.includes(normalizeKey(field.fieldName))) ??
  formConfig.find((field) => names.includes(normalizeKey(field.fieldLabel)));

const getPointInTimeRestoreField = (formConfig) =>
  findStorageSetting(formConfig, [
    "pointintimerestore", "enablepointintimerestore", "enablepointintimerestoreforcontainers",
    "pointintimerestoreenabled", "restorepolicyenabled", "enablepitr", "pitrenabled", "pitr",
  ]);

export const getStoragePointInTimeRestoreErrors = (formConfig, formData) => {
  const restore = getPointInTimeRestoreField(formConfig);
  if (!restore || !isChecked(getStorageFieldValue(restore, formData))) return [];

  const errors = [];
  const add = (field, message) => errors.push({
    fieldName: (field || restore).fieldName,
    message,
  });

  const setting = (names) => findStorageSetting(formConfig, names);
  const selected = (field) => normalizeKey(
    formData?.[field.fieldName]?.label || getStorageFieldValue(field, formData)
  );

  const restoreDays = setting([
    "restoredays", "restorepolicydays", "pointintimerestoredays", "pointintimerestoreretentiondays",
    "maximumrestorepointdaysago", "maximumrestorepoint", "pitrretentiondays", "pointintimerestoreretention",
  ]);
  const softDeleteDays = setting([
    "blobsoftdeletedays", "blobsoftdeleteretentiondays", "softdeleteretentiondays",
    "deleteretentionpolicydays", "daystoretaindeletedblobs", "blobretentiondays", "softdeletedays",
  ]);

  const restoreValue = restoreDays && String(getStorageFieldValue(restoreDays, formData)).trim();
  const softDeleteValue = softDeleteDays && String(getStorageFieldValue(softDeleteDays, formData)).trim();

  if (!restoreDays || !/^\d+$/.test(restoreValue) || Number(restoreValue) < 1 || Number(restoreValue) > 360) {
    add(restoreDays, "Maximum restore point must be a whole number from 1-360 days.");
  }
  if (!softDeleteDays || !/^\d+$/.test(softDeleteValue) || Number(softDeleteValue) < 1 || Number(softDeleteValue) > 365) {
    add(softDeleteDays, "Blob soft delete retention must be a whole number from 1-365 days.");
  } else if (Number(restoreValue) >= Number(softDeleteValue)) {
    add(restoreDays, "Maximum restore point must be less than blob soft delete retention. For 30 restore days, retain deleted blobs for at least 31 days.");
  }

  const performance = setting(["performance", "performancetier", "accountperformance"]);
  const sku = setting(["sku", "skuname"]);
  if (
    (performance && selected(performance) !== "standard") ||
    (sku && selected(sku).startsWith("premium"))
  ) {
    add(performance || sku, "Point-in-time restore requires Standard performance; Premium storage is not supported.");
  }

  const kind = setting(["accountkind", "storageaccountkind", "kind", "storageaccounttype"]);
  if (
    kind &&
    !["storagev2", "generalpurposev2", "standardgeneralpurposev2", "storagev2generalpurposev2"].includes(selected(kind))
  ) {
    add(kind, "Point-in-time restore requires a general-purpose v2 (StorageV2) account.");
  }

  const namespace = setting([
    "ishnsenabled", "hnsenabled", "enablehierarchicalnamespace", "hierarchicalnamespace",
    "hierarchicalnamespaceenabled",
  ]);
  if (namespace && isChecked(getStorageFieldValue(namespace, formData))) {
    add(namespace, "Point-in-time restore cannot be enabled with hierarchical namespace (Data Lake Storage).");
  }

  const immutability = setting([
    "enableversionlevelimmutabilitysupport", "versionlevelimmutabilitysupport",
    "versionlevelimmutability", "immutablestoragewithversioningenabled", "versionlevelimmutabilityenabled",
  ]);
  if (immutability && isChecked(getStorageFieldValue(immutability, formData))) {
    add(immutability, "Version-level immutability support and point-in-time restore cannot be enabled simultaneously.");
  }

  return errors;
};

const getStorageRadioOptions = (field) => {
  let rawOptions = [];

  if (field?.dropdownValues) {
    try {
      rawOptions = JSON.parse(field.dropdownValues);
    } catch {
      rawOptions = [];
    }
  }

  if (!Array.isArray(rawOptions) || rawOptions.length === 0) {
    rawOptions = field?.dropdownSource
      ? String(field.dropdownSource)
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : [];
  }

  return rawOptions
    .map((option) => {
      if (option && typeof option === "object") {
        const optionValue = option.value ?? option.id ?? "";
        const optionLabel = option.label ?? option.text ?? String(optionValue);

        return {
          value: optionValue,
          label: optionLabel,
          isEnable: option.isEnable ?? option.isEnabled ?? true,
        };
      }

      const trimmed = String(option).trim();
      return {
        value: trimmed,
        label: trimmed,
        isEnable: true,
      };
    })
    .filter((option) => String(option.value ?? "").trim() !== "");
};

export const validateAzureStorageAccountForm = async ({
  formConfig,
  formData,
  tabName,
  shouldHideField,
  checkAvailability = false,
  accountId,
  resourceId = 0,
  resourceType,
}) => {
  const errors = [];
  const restoreField = getPointInTimeRestoreField(formConfig);
  if (!tabName || restoreField?.tabName === tabName ||
    formConfig.some((field) => field.tabName === tabName && isStorageDataProtectionField(field))) {
    errors.push(...getStoragePointInTimeRestoreErrors(formConfig, formData));
  }

  const fieldsToCheck = formConfig.filter((field) => {
    if (field.isDisplay === false || field.fieldType === "collection") return false;
    if (tabName && field.tabName !== tabName) return false;
    return !shouldHideField?.(field, formData);
  });

  for (const field of fieldsToCheck) {
    if (field.fieldType === "checkbox") {
      const { message } = getStorageCheckboxState(field, formConfig, formData);
      if (message) errors.push({ fieldName: field.fieldName, message });
      continue;
    }
    const daysError = getStorageDaysError(field, formData);
    if (daysError) {
      errors.push({ fieldName: field.fieldName, message: daysError });
      continue;
    }
    if (!field.isRequired) continue;

    const value = getStorageFieldValue(field, formData);
    const missing =
      field.fieldType === "checkbox"
        ? !isChecked(value)
        : isEmptyValue(value);

    if (missing) {
      errors.push({
        fieldName: field.fieldName,
        message: `${field.fieldLabel || field.fieldName} is required.`,
      });
    }
  }

  const nameField = fieldsToCheck.find((field) =>
    [field.fieldName, field.fieldLabel].some(
      (value) => normalizeKey(value) === "storageaccountname"
    )
  );

  const nameFieldName = nameField?.fieldName || "storageAccountName";
  const rawName = nameField
    ? getStorageFieldValue(nameField, formData)
    : formData?.[nameFieldName]?.value;

  const storageAccountName = String(rawName ?? "").trim();

  const storageNameError = nameField
    ? getAzureStorageAccountNameError(storageAccountName)
    : "";
  if (storageNameError) {
    errors.push({
      fieldName: nameFieldName,
      message: storageNameError,
    });
  }

  const ipField = fieldsToCheck.find((field) => isAzureIpv4Field(field.fieldName));
  if (ipField && !shouldHideField?.(ipField, formData)) {
    const ipValue = getStorageFieldValue(ipField, formData);
    const ipError = getAzureIpv4OrCidrError(ipValue);
    if (ipError) {
      errors.push({
        fieldName: ipField.fieldName,
        message: ipError,
      });
    }
  }

  if (errors.length > 0 || !checkAvailability || !nameField) {
    return { isValid: errors.length === 0, errors };
  }

  try {
    const { data } = await api.get(
      "/api/AwsEc2Data/ValidateResourceCreation",
      {
        params: {
          resourceName: storageAccountName,
          resourceId,
          accountId,
          resourceType,
        },
        headers: {
          "X-CORRELATION-ID": crypto.randomUUID(),
          accept: "application/json",
        },
      }
    );

    if (data?.isValid !== true) {
      errors.push({
        fieldName: data?.fieldName || nameFieldName,
        message: data?.reason || "Storage account name validation failed.",
      });
    }
  } catch (error) {
    errors.push({
      fieldName: nameFieldName,
      message:
        error.response?.data?.reason ||
        error.message ||
        "Could not validate the storage account name.",
    });
  }

  return { isValid: errors.length === 0, errors };
};



export const isStorageFieldVisible = (field, formConfig, formData, ancestors = new Set()) => {
  const fieldKey = String(field.fieldName).trim().toLowerCase();
  if (ancestors.has(fieldKey)) {
    console.error("Circular storage field dependency:", field.fieldName);
    return false;
  }
  if (field.isDisplay === false) return false;

  const dependencies = splitFieldNames(field.dependentOn);
  if (!dependencies.length) return true;

  const nextAncestors = new Set(ancestors);
  nextAncestors.add(fieldKey);
  const expectedValues = splitFieldNames(field.dependentValue).map((value) => value.toLowerCase());

  return dependencies.every((dependencyName) => {
    const parent = formConfig.find(
      (candidate) =>
        String(candidate.fieldName).trim().toLowerCase() === dependencyName.toLowerCase()
    );
    if (!parent) {
      console.error("Missing storage field dependency:", field.fieldName, dependencyName);
      return false;
    }
    if (!isStorageFieldVisible(parent, formConfig, formData, nextAncestors)) return false;

    const value = getStorageFieldValue(parent, formData);
    const normalizedValue = String(value).trim().toLowerCase();
    return expectedValues.length
      ? expectedValues.includes(normalizedValue)
      : normalizedValue !== "" && normalizedValue !== "false";
  });
};

const AzureStorageAccountCreation = ({
  activeTabName,
  formConfig,
  formData,
  renderField,
  shouldHideField,
  renderTagsSection,
  handleChange,
  disabledFields = {},
  showValidation,
}) => {
  const restoreField = getPointInTimeRestoreField(formConfig);
  const restoreEnabled = restoreField && isChecked(getStorageFieldValue(restoreField, formData));
  const restoreErrors = getStoragePointInTimeRestoreErrors(formConfig, formData);
  React.useEffect(() => {
    const checkboxFields = formConfig.filter((field) => field.fieldType === "checkbox");

    checkboxFields.forEach((field) => {
      if (field.isDisplay === false || field.fieldName === restoreField?.fieldName) return;

      const currentValue = isChecked(getStorageFieldValue(field, formData));
      const { checked } = getStorageCheckboxState(field, formConfig, formData);
      if (currentValue !== checked) {
        handleChange(field.fieldName, checked, field.fieldLabel);
      }
    });
  }, [formConfig, formData, handleChange, restoreField]);

  const renderDaysInput = (field) => {
    const value = getStorageFieldValue(field, formData);
    
    const restoreError = restoreErrors.find((error) => error.fieldName === field.fieldName)?.message;
    const message = restoreError || getStorageDaysError(field, formData);
    const isError = Boolean(restoreError) || (showValidation && Boolean(message));

    return (
      <TextField
        name={field.fieldName}
        label={field.fieldLabel || field.fieldName}
        variant="outlined"
        size="small"
        value={value}
        onChange={(event) => {
          handleChange(field.fieldName, event.target.value, field.fieldLabel);
        }}
        inputProps={{ inputMode: "numeric" }}
        required={field.isRequired}
        disabled={field.isEnabled === false || disabledFields[field.fieldName] === true}
        error={isError}
        helperText={isError ? message : ""}
        fullWidth
      />
    );
  };

  const renderStorageRadioField = (field) => {
    const currentValue = getStorageFieldValue(field, formData);
    const options = getStorageRadioOptions(field);
    const isMissing =
      showValidation &&
      field.isRequired &&
      (currentValue === undefined || currentValue === null || String(currentValue).trim() === "");

    return (
      <FormRadio
        name={field.fieldName}
        label={field.fieldLabel || field.fieldName}
        options={options}
        value={String(currentValue ?? "")}
        required={field.isRequired}
        disabled={field.isEnabled === false || disabledFields[field.fieldName] === true}
        error={isMissing}
        helperText={isMissing ? "This field is required" : ""}
        onChange={(event) => {
          const selectedValue = event?.target?.value ?? "";
          const selectedOption = options.find(
            (option) => String(option.value) === String(selectedValue)
          );

          handleChange(
            field.fieldName,
            selectedOption?.value ?? selectedValue,
            selectedOption?.label ?? String(selectedValue)
          );
        }}
      />
    );
  };

  const renderStorageField = (field) => {
    if (String(field?.fieldType ?? "").toLowerCase() === "radio button") {
      return renderStorageRadioField(field);
    }

    return renderField(field);
  };

  const renderDataProtectionRow = (node) => {
    const isCheckbox = node.fieldType === "checkbox";
    const isDays = isNumericDaysInput(node);

    const renderedSelf = isDays && !isCheckbox
      ? renderDaysInput(node)
      : renderStorageField(node);

    if (!renderedSelf) return null;

    const inlineChildren = node.children || [];

    return (
      <Box
        key={node.fieldId ?? node.fieldName}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
          gap: 2,
          mb: 1.5,
          minHeight: "48px",
        }}
      >
        <Box
          sx={{
            flex: isCheckbox ? "0 0 400px" : isDays ? "0 0 280px" : "1 1 auto",
            maxWidth: isCheckbox ? "400px" : isDays ? "280px" : "100%",
            ...(isCheckbox && {
              "& .MuiCheckbox-root": { padding: "2px 9px" },
            }),
          }}
        >
          {renderedSelf}
        </Box>

        {inlineChildren.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
              flex: "1 1 auto",
            }}
          >
            {inlineChildren.map((child) => {
              const isChildDays = isNumericDaysInput(child);
              const renderedChild = isChildDays
                ? renderDaysInput(child)
                : renderStorageField(child);

              if (!renderedChild) return null;

              return (
                <Box
                  key={child.fieldId ?? child.fieldName}
                  sx={{ flex: isChildDays ? "0 0 320px" : "0 0 320px" }}
                >
                  {renderedChild}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const fieldGroups = new Map();

  formConfig
    .filter(
      (field) =>
        field.tabName === activeTabName &&
        field.fieldType !== "collection" &&
        !shouldHideField(field, formData)
    )
    .forEach((field) => {
      const groupName = field.subTab || "";
      if (!fieldGroups.has(groupName)) fieldGroups.set(groupName, []);
      fieldGroups.get(groupName).push(field);
    });

  const groupEntries = Array.from(fieldGroups);
  const hasGeneralGroup = groupEntries.some(
    ([groupName]) => String(groupName).trim().toLowerCase() === "general"
  );

  return (
    <Box sx={{ width: "100%", mt: 2, color: "text.primary" }}>
      {activeTabName === "Data Protection" && restoreEnabled && (
        <Box sx={{ mb: 2 }}>
          {restoreErrors.map((error) => (
            <Alert key={`${error.fieldName}-${error.message}`} severity="error" sx={{ mb: 1 }}>
              {error.message}
            </Alert>
          ))}
          <Alert severity="info">
            Point-in-time restore applies to Hot/Cool block blobs, not deleted containers.
            Archived blobs in a selected container can fail the entire restore. Active leases
            can also fail a restore when the blob version differs from the target timestamp.
            Azure must verify subscription, region, replication and account support; these
            checks and the contents of existing containers cannot be verified by this form.
          </Alert>
        </Box>
      )}
      {groupEntries.map(([groupName, fields], groupIndex) => {
        const shouldRenderTagSection =
          activeTabName === "Tag" && (
            String(groupName).trim().toLowerCase() === "general" ||
            (!hasGeneralGroup && groupIndex === 0)
          );

        return (
          <Box key={groupName || "default"} sx={{ width: "100%", mb: 3 }}>
            {groupName && groupName.toLowerCase() !== "general" && (
              <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
                {groupName}
              </Typography>
            )}

            {activeTabName === "Data Protection" ? (
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                {(() => {
                  const rootNodes = [];
                  const nodeMap = new Map();

                  fields.forEach((field) => {
                    nodeMap.set(String(field.fieldName).toLowerCase(), { ...field, children: [] });
                  });

                  fields.forEach((field) => {
                    const fieldKey = String(field.fieldName).toLowerCase();
                    const node = nodeMap.get(fieldKey);

                    if (field.fieldType === "checkbox") {
                      rootNodes.push(node);
                    } 
                    else {
                      const depKeys = field.dependentOn 
                        ? String(field.dependentOn).split(",").map(k => k.trim().toLowerCase()).filter(Boolean)
                        : [];

                      let parentNode = null;
                      let isParentHiddenButInTab = false;

                      for (const dKey of depKeys) {
                        if (nodeMap.has(dKey)) {
                          const potentialParent = nodeMap.get(dKey);
                          if (potentialParent.fieldType === 'checkbox') {
                            parentNode = potentialParent;
                            break; 
                          }
                        } else {
                          const parentInConfig = formConfig.find(
                            (f) => String(f.fieldName).toLowerCase() === dKey
                          );
                          if (parentInConfig && parentInConfig.tabName === activeTabName) {
                            isParentHiddenButInTab = true;
                          }
                        }
                      }

                      if (parentNode) {
                        parentNode.children.push(node);
                      } else if (!isParentHiddenButInTab) {
                        rootNodes.push(node);
                      }
                    }
                  });

                  return rootNodes.map((node) => renderDataProtectionRow(node));
                })()}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  width: "100%",
                }}
              >
                {fields.map((field, index) => {
                  const renderedField = renderStorageField(field);
                  
                  if (!renderedField) return null;

                  return (
                    <Box
                      key={field.fieldId ?? `${field.fieldName}-${index}`}
                      sx={{
                        flex:
                          field.fieldName === "masterUsername" ||
                          field.fieldType === "checkbox"
                            ? "1 1 100%"
                            : "1 1 calc(50% - 16px)",
                        minWidth: "280px",
                        ...(field.fieldType === "checkbox" && {
                          "& .MuiCheckbox-root": { padding: "2px 9px" },
                        }),
                      }}
                    >
                      {renderedField}
                    </Box>
                  );
                })}
              </Box>
            )}

            {shouldRenderTagSection && renderTagsSection()}
          </Box>
        );
      })}
    </Box>
  );
};

export default AzureStorageAccountCreation;